import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import axios from "axios";
import { useQuote } from "../context/QuoteContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

const fields = [
  { name: "name", label: "Full Name", required: true, type: "text" },
  { name: "company", label: "Company", type: "text" },
  { name: "email", label: "Email", required: true, type: "email" },
  { name: "phone", label: "Phone", type: "tel" },
  { name: "product", label: "Product / Requirement", type: "text" },
  { name: "quantity", label: "Estimated Quantity", type: "text" },
];

const empty = { name: "", company: "", email: "", phone: "", product: "", quantity: "", message: "" };

export default function QuoteModal() {
  const { open, prefill, closeQuote } = useQuote();
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [hp, setHp] = useState("");
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (open) {
      setForm({ ...empty, product: prefill || "" });
      setStatus("idle");
      setError("");
      setHp("");
      startRef.current = Date.now();
    }
  }, [open, prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { setError("Please provide your name and email."); return; }
    setStatus("loading"); setError("");
    try {
      await axios.post(`${API}/quotes`, { ...form, website: hp, elapsed_ms: Date.now() - startRef.current });
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong. Please try again or email us directly.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[100] bg-[#0A1128]/60 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={closeQuote}
          />
          <motion.aside
            className="fixed top-0 right-0 z-[101] h-full w-full sm:w-[480px] bg-white overflow-y-auto"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.55, ease }}
            data-testid="quote-panel"
          >
            <div className="sticky top-0 bg-[#0A1128] text-white px-8 py-6 flex items-center justify-between z-10">
              <div>
                <p className="text-[10px] font-bold tracking-[0.3em] text-[#38bdf8]">CLINIXPORT GLOBAL</p>
                <h3 className="text-xl font-extrabold tracking-tight mt-1">Request a Quote</h3>
              </div>
              <button onClick={closeQuote} data-testid="quote-close" aria-label="Close" className="p-1 hover:text-[#38bdf8]">
                <X size={24} />
              </button>
            </div>

            {status === "success" ? (
              <div className="p-8 flex flex-col items-center text-center py-24" data-testid="quote-success">
                <div className="w-16 h-16 bg-[#0D9488] flex items-center justify-center mb-6">
                  <Check size={32} className="text-white" />
                </div>
                <h4 className="text-2xl font-extrabold tracking-tight text-[#0A1128]">Enquiry received</h4>
                <p className="mt-3 text-[#475569] leading-relaxed max-w-xs">
                  Thank you. Our team will review your requirement and get back to you shortly.
                </p>
                <button onClick={closeQuote} className="mt-8 border border-[#0A1128] px-8 py-3.5 font-bold text-xs tracking-widest uppercase hover:bg-[#0A1128] hover:text-white transition-all">
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-8 space-y-5" data-testid="quote-form">
                <input
                  type="text" name="website" value={hp} onChange={(e) => setHp(e.target.value)}
                  tabIndex={-1} autoComplete="off" aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                />
                <p className="text-[#475569] text-[15px] leading-relaxed">
                  Tell us what you need. No prices or checkout — just a fast, professional response from our supply team.
                </p>
                {fields.map((f) => (
                  <div key={f.name}>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">
                      {f.label}{f.required && <span className="text-[#1F6FEB]"> *</span>}
                    </label>
                    <input
                      type={f.type} value={form[f.name]} onChange={set(f.name)}
                      data-testid={`quote-${f.name}`}
                      className="w-full bg-white border border-[#0A1128]/20 px-4 py-3 text-[#0A1128] outline-none focus:border-[#1F6FEB] transition-colors"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">Message</label>
                  <textarea
                    rows={4} value={form.message} onChange={set("message")}
                    data-testid="quote-message"
                    className="w-full bg-white border border-[#0A1128]/20 px-4 py-3 text-[#0A1128] outline-none focus:border-[#1F6FEB] transition-colors resize-none"
                  />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="quote-error">{error}</p>}
                <button
                  type="submit" disabled={status === "loading"}
                  data-testid="quote-submit"
                  className="w-full bg-[#1F6FEB] text-white hover:bg-[#1556B8] transition-colors px-8 py-4 font-bold text-sm tracking-widest uppercase disabled:opacity-60"
                >
                  {status === "loading" ? "Sending…" : "Submit Request"}
                </button>
              </form>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
